import React, { useEffect, useRef, useState } from "react";

/**
 * Ambient 7.83Hz Schumann-inspired drone.
 * Pure 7.83Hz is below human hearing, so we use a binaural beat:
 * - left ear: 136.1Hz (often called the "OM frequency")
 * - right ear: 143.93Hz  (difference = 7.83Hz)
 * The brain perceives the 7.83Hz pulse as a gentle breathing in the room.
 */
export default function AmbientDrone() {
  const ctxRef = useRef(null);
  const nodesRef = useRef({});
  const [on, setOn] = useState(false);

  // hydrate from localStorage
  useEffect(() => {
    try {
      const saved = localStorage.getItem("grove_drone");
      if (saved === "on") {
        // don't auto-start (browsers block until user gesture); just remember
      }
    } catch (e) {
      // localStorage may be unavailable in private mode — silent OK on hydrate
      console.debug("drone hydrate skipped:", e?.message);
    }
  }, []);

  const start = async () => {
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) return;
      const ctx = new AC();
      await ctx.resume();
      const master = ctx.createGain();
      master.gain.value = 0;
      master.connect(ctx.destination);

      const leftOsc = ctx.createOscillator();
      const rightOsc = ctx.createOscillator();
      leftOsc.type = "sine";
      rightOsc.type = "sine";
      leftOsc.frequency.value = 136.1;
      rightOsc.frequency.value = 143.93;

      const merger = ctx.createChannelMerger(2);
      const leftGain = ctx.createGain();
      const rightGain = ctx.createGain();
      leftGain.gain.value = 0.5;
      rightGain.gain.value = 0.5;

      leftOsc.connect(leftGain).connect(merger, 0, 0);
      rightOsc.connect(rightGain).connect(merger, 0, 1);
      merger.connect(master);

      leftOsc.start();
      rightOsc.start();

      // slow fade in to 0.08 (gentle)
      const now = ctx.currentTime;
      master.gain.setValueAtTime(0, now);
      master.gain.linearRampToValueAtTime(0.08, now + 3);

      ctxRef.current = ctx;
      nodesRef.current = { master, leftOsc, rightOsc };
      setOn(true);
      localStorage.setItem("grove_drone", "on");
    } catch (e) {
      console.warn("ambient drone failed", e);
    }
  };

  const stop = () => {
    const ctx = ctxRef.current;
    const { master, leftOsc, rightOsc } = nodesRef.current || {};
    if (!ctx) {
      setOn(false);
      return;
    }
    try {
      const now = ctx.currentTime;
      master.gain.cancelScheduledValues(now);
      master.gain.setValueAtTime(master.gain.value, now);
      master.gain.linearRampToValueAtTime(0, now + 1.5);
      setTimeout(() => {
        try {
          leftOsc.stop();
          rightOsc.stop();
          ctx.close();
        } catch (e) {
          console.debug("drone teardown noise:", e?.message);
        }
      }, 1700);
    } catch (e) {
      console.warn("drone fade-out failed:", e?.message);
    }
    ctxRef.current = null;
    nodesRef.current = {};
    setOn(false);
    localStorage.setItem("grove_drone", "off");
  };

  const toggle = () => (on ? stop() : start());

  // cleanup on unmount
  useEffect(() => () => stop(), []); // eslint-disable-line

  return (
    <button
      onClick={toggle}
      data-testid="drone-toggle"
      className="fixed bottom-6 left-6 z-40 glass rounded-full px-4 py-2 text-xs tracking-[0.2em] uppercase text-[#1C241F] hover:bg-[#EAE5D9] transition-all duration-500 flex items-center gap-2"
      title={on ? "hush the drone" : "play 7.83Hz ambient drone (binaural)"}
    >
      <span
        className={`inline-block w-2 h-2 rounded-full ${
          on ? "bg-[#B85C38] breathe" : "bg-[#9FA9A3]"
        }`}
      />
      {on ? "drone · breathing" : "drone · still"}
    </button>
  );
}
